// frontend/src/pages/BlogPostPage.jsx
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, User, Clock, Eye, Share2, Heart, MessageCircle, ArrowRight, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { PageSEO } from '../components/SEO/PageSEO';
import api from '../services/api';

export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [comment, setComment] = useState('');
  const [liked, setLiked] = useState(false);
  
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/cms/blog/${slug}/`);
        setPost(response.data);
        
        const relatedResponse = await api.get('/cms/blog/', {
          params: { limit: 3, exclude: slug }
        });
        setRelatedPosts(relatedResponse.data.results?.filter(p => p.slug !== slug) || []);
        
        await api.post(`/cms/blog/${slug}/view/`).catch(() => {});
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setPost(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPost();
  }, [slug]);

  const handleLike = async () => {
    setLiked(!liked);
    try {
      await api.post(`/cms/blog/${slug}/like/`);
    } catch (error) {
      console.error('Error liking post:', error);
      setLiked(!liked);
    }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/cms/blog/${slug}/comment/`, { content: comment });
      toast.success('نظر شما با موفقیت ثبت شد');
      setComment('');
      const response = await api.get(`/cms/blog/${slug}/`);
      setPost(response.data);
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('خطا در ثبت نظر');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">مقاله یافت نشد</h2>
          <p className="text-gray-600 mb-6">متأسفیم، مقاله مورد نظر شما وجود ندارد.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700">
            بازگشت به وبلاگ <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const postImage = post.featured_image || '/placeholder-image.jpg';
  const seoTitle = post.meta_title || post.title;
  const seoDescription = post.meta_description || post.excerpt || post.content?.substring(0, 160);
  const seoKeywords = post.meta_keywords;

  return (
    <>
      <PageSEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={postImage}
        type="article"
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* مسیر راهنما */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 flex-wrap">
            <button onClick={() => navigate('/')} className="hover:text-blue-600">خانه</button>
            <ChevronRight className="w-4 h-4" />
            <button onClick={() => navigate('/blog')} className="hover:text-blue-600">وبلاگ</button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium line-clamp-1">{post.title}</span>
          </div>

          <article className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* تصویر شاخص - اصلاح شده */}
            {post.featured_image && (
              <img 
                src={post.featured_image} 
                alt={post.title} 
                className="w-full h-96 object-cover"
                onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
              />
            )}

            <div className="p-6 md:p-8">
              {/* متادیتا */}
              <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                  {post.page_type === 'blog' ? 'مقاله' : (post.page_type || 'مطلب')}
                </span>
                <div className="flex items-center gap-1 text-gray-500">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.published_at).toLocaleDateString('fa-IR')}
                </div>
                <div className="flex items-center gap-1 text-gray-500">
                  <Clock className="w-4 h-4" />
                  {post.read_time || Math.ceil((post.content?.length || 0) / 1000)} دقیقه مطالعه
                </div>
                <div className="flex items-center gap-1 text-gray-500">
                  <Eye className="w-4 h-4" />
                  {post.view_count || 0} بازدید
                </div>
              </div>

              {/* عنوان */}
              <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
                {post.title}
              </h1>

              {/* نویسنده و اشتراک‌گذاری */}
              <div className="flex flex-wrap items-center justify-between py-4 border-y border-gray-100 mb-6 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {post.author?.username || 'مدیر سایت'}
                    </p>
                    <p className="text-xs text-gray-500">نویسنده</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={handleLike}
                    className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition ${liked ? 'bg-red-50 border-red-200 text-red-500' : 'hover:bg-gray-50'}`}
                  >
                    <Heart className={`w-4 h-4 ${liked ? 'fill-red-500 text-red-500' : ''}`} />
                    {post.likes_count || 0}
                  </button>
                  <button className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 transition">
                    <Share2 className="w-4 h-4" />
                    اشتراک‌گذاری
                  </button>
                </div>
              </div>

              {/* محتوا */}
              <div 
                className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.content || '<p>محتوایی برای این مقاله وجود ندارد.</p>' }}
              />

              {/* تگ‌ها */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-8 pt-4 border-t">
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-600 text-sm px-3 py-1 rounded-full">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>

          {/* نظرات */}
          <div className="mt-8 bg-white rounded-xl shadow-sm p-6 md:p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              دیدگاه کاربران ({post.comments?.length || 0})
            </h3>
            
            {post.comments && post.comments.length > 0 ? (
              <div className="space-y-4 mb-6">
                {post.comments.map((commentItem, idx) => (
                  <div key={idx} className="border-b pb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-500" />
                      </div>
                      <span className="font-medium">{commentItem.user?.username || 'کاربر'}</span>
                      <span className="text-xs text-gray-400">
                        {new Date(commentItem.created_at).toLocaleDateString('fa-IR')}
                      </span>
                    </div>
                    <p className="text-gray-600">{commentItem.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 mb-4">هنوز نظری ثبت نشده است. اولین نفری باشید که نظر می‌دهد!</p>
            )}

            {/* فرم نظر */}
            <div className="mt-4">
              <textarea
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="نظر خود را بنویسید..."
              />
              <button 
                onClick={handleComment}
                disabled={!comment.trim()}
                className="mt-3 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                ارسال دیدگاه
              </button>
            </div>
          </div>

          {/* مطالب مرتبط */}
          {relatedPosts.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">مطالب مرتبط</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((related) => (
                  <Link 
                    key={related.id} 
                    to={`/blog/${related.slug}`} 
                    className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition group"
                  >
                    <img 
                      src={related.featured_image || '/placeholder-image.jpg'} 
                      alt={related.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition duration-300"
                      onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
                    />
                    <div className="p-4">
                      <h4 className="font-bold text-gray-900 group-hover:text-blue-600 transition line-clamp-2">
                        {related.title}
                      </h4>
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                        {related.excerpt || 'برای مطالعه کلیک کنید...'}
                      </p>
                      <div className="flex items-center gap-2 mt-3 text-xs text-gray-400">
                        <Calendar className="w-3 h-3" />
                        {new Date(related.published_at).toLocaleDateString('fa-IR')}
                        <Eye className="w-3 h-3 mr-2" />
                        {related.view_count || 0}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};